/* ─────────────────────────────────────────────────────────────────────────
 * Loupe — a drop of glass on a page of type.
 *
 * One held frame: a masthead, one enormous line, a measuring grid lying down
 * and receding, and a bead of living glass that drifts across the words and
 * BENDS them. There is no sequence, no gallery and no scroll — the whole piece
 * is the glass and what it does to the type underneath it.
 *
 * The glass is a real lens, not a frosted disc. It holds a second, live copy of
 * the entire scene, magnified about the bead's own centre and clipped to the
 * bead's outline — so the letters under it genuinely swell and shift sideways.
 *
 *   `backdrop-filter: blur()` cannot do this. Blur is not refraction: it frosts
 *   what is behind and magnifies NOTHING, so type under it turns to fog instead
 *   of growing — the one thing the effect exists to do. An SVG
 *   `feDisplacementMap` can bend pixels, but driving it needs
 *   `backdrop-filter: url(#…)`, whose support is uneven and which fails
 *   SILENTLY: the piece would lose its subject on some browsers with nothing in
 *   the console. A duplicated subtree is honest geometry and works everywhere.
 *
 * The bead is never a circle and never still. Its outline kneads itself
 * continuously, and it carries lights inside it — green, pink and a warm amber
 * — orbiting on their own slow periods. It follows the cursor with weight; with
 * no cursor it wanders the frame on its own, because a piece of glass that only
 * moves when a mouse does is dead on a phone and dead in a screenshot.
 *
 * ONE requestAnimationFrame clock owns the outline, the drift, the lights and
 * the lens transform. GSAP owns only the entrance.
 *
 * The section fills its positioned parent, and that parent must sit at the
 * viewport's top-left: the bead follows `clientX/clientY` and every size is
 * read off the window.
 * ───────────────────────────────────────────────────────────────────────── */

import type { CSSProperties, FC, ReactNode } from 'react'
import type { GlassFilterRefs } from '@/lib/liquid-glass'

import { gsap } from 'gsap'

import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from 'react'

import { cn } from '@/lib/utils'
import { applyGlassMap, makeGlassMap } from '@/lib/liquid-glass'
import { COPY } from '@/constants/loupe'

/* ── the room ──────────────────────────────────────────────────────────────
 * An instrument room: pale at the top, deepening toward the floor, with the
 * grid receding behind the type.
 *
 * The house rule is no saturated colour and no neon, and it holds. The colour
 * only appears as the ground DEEPENS — it is never a field of colour. The
 * saturation in the frame lives inside the bead, which is the one object that
 * is meant to look lit. */
type Theme = 'light' | 'dark'

interface Palette {
    field: string
    band: string
    grid: string
    ink: string
    sub: string
    line: string
    edge: string
    contact: string
    blend: CSSProperties['mixBlendMode']
    contour: string
    veil: string
    rimLit: string
    rimMid: string
    rimFar: string
}

const PALETTE: Record<Theme, Palette> = {
    light: {
        field: '#FBFCFA',
        // stops are relative to the EXTENDED element (110% of the viewport), so
        // 50/76/100% of the frame land at 45.45/69.09/90.91% of the box
        band: 'linear-gradient(180deg, rgba(160,220,104,0) 45.45%, rgba(150,222,88,0.48) 69.09%, rgba(132,226,56,0.76) 90.91%)',
        grid: 'rgba(20,18,26,0.22)',
        ink: '#121016',
        sub: 'rgba(18,16,22,0.55)',
        line: 'rgba(18,16,22,0.14)',
        edge: 'radial-gradient(circle at 50% 34%, rgba(255,255,255,0.14), rgba(255,255,255,0) 46%), radial-gradient(circle at 50% 118%, rgba(22,44,30,0.2), rgba(22,44,30,0) 54%), radial-gradient(circle at 50% 50%, rgba(44,76,52,0) 58%, rgba(44,76,52,0.1) 80%, rgba(32,60,40,0.26) 93%, rgba(24,48,32,0.4) 100%)',
        contact: '0 20px 30px -6px rgba(56,118,40,0.32), 0 3px 8px -2px rgba(56,118,40,0.24)',
        contour: 'rgba(30,58,38,0.22)',
        // the ground colour, laid back over the lattice's corners
        veil: 'radial-gradient(ellipse 58% 30% at 50% 58%, rgba(251,252,250,0) 0%, rgba(251,252,250,0) 55%, rgba(251,252,250,0.52) 76%, rgba(251,252,250,0.89) 91%, rgba(251,252,250,1) 100%)',
        rimLit: 'rgba(255,255,255,0.92)',
        rimMid: 'rgba(24,48,32,0.2)',
        rimFar: 'rgba(24,48,32,0.4)',
        // coloured glass on a pale ground TINTS; it cannot glow
        blend: 'multiply'
    },
    dark: {
        field: '#121511',
        band: 'linear-gradient(180deg, rgba(92,162,62,0) 45.45%, rgba(104,182,68,0.42) 69.09%, rgba(128,214,72,0.62) 90.91%)',
        grid: 'rgba(236,228,242,0.24)',
        ink: '#F0EAF3',
        sub: 'rgba(240,234,243,0.55)',
        line: 'rgba(240,234,243,0.16)',
        edge: 'radial-gradient(circle at 50% 34%, rgba(255,255,255,0.1), rgba(255,255,255,0) 46%), radial-gradient(circle at 50% 118%, rgba(0,0,0,0.3), rgba(0,0,0,0) 54%), radial-gradient(circle at 50% 50%, rgba(0,0,0,0) 58%, rgba(0,0,0,0.18) 80%, rgba(0,0,0,0.38) 93%, rgba(0,0,0,0.54) 100%)',
        contact: '0 20px 30px -6px rgba(0,0,0,0.55), 0 3px 8px -2px rgba(0,0,0,0.4)',
        contour: 'rgba(236,228,242,0.18)',
        veil: 'radial-gradient(ellipse 58% 30% at 50% 58%, rgba(18,21,17,0) 0%, rgba(18,21,17,0) 55%, rgba(18,21,17,0.52) 76%, rgba(18,21,17,0.89) 91%, rgba(18,21,17,1) 100%)',
        rimLit: 'rgba(255,255,255,0.95)',
        rimMid: 'rgba(255,255,255,0.18)',
        rimFar: 'rgba(0,0,0,0.3)',
        // on a dark ground the same lights GLOW
        blend: 'screen'
    }
}

/* Client only: read from effects, never during render, so the server and the
 * first client render always agree on the light room. */
const readTheme = (): Theme =>
    document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'

/* the lights inside the glass — ONE set of hues in both themes, so the bead is
 * the same object in a lit room and a dark one; only the blend changes */
const LIGHT_GREEN = 'rgba(52,224,158,'
const LIGHT_PINK = 'rgba(255,104,190,'
/* Greyed deliberately: multiplying over black ink, a hotter amber was the most
 * saturated object in a frame that is meant to be desaturated. */
const LIGHT_AMBER = 'rgba(224,168,120,' // the third: a warm break between them

/* ── how a light is built ─────────────────────────────────────────────────
 * Each light is a soft blob riding its own orbit, and two things about the
 * geometry are load-bearing.
 *
 * ITS BOX MUST OVERHANG THE DISC by more than the orbit's reach. A radial
 * gradient is cut off by its own element box, and at these offsets the fade
 * had not reached zero by the time it met that edge — so the corner of the box
 * printed a hard SQUARE inside the circle. The box hangs LIGHT_PAD past the
 * disc on every side, and the mask wrapper clips the overhang (its square edge
 * is tangent to the circle, so clipping there removes nothing the disc was not
 * already hiding).
 *
 * AND THE RADIUS IS EXPLICIT (`ellipse R% R%`) rather than left to the default
 * `farthest-corner`, or enlarging the box would enlarge the light with it.
 *
 * The falloff is a six-stop approximation of a gaussian. Three stops leave a
 * visible shoulder where the curve changes slope, and on a blob this large and
 * this slow that shoulder reads as an edge in its own right. */
const LIGHT_PAD = 0.45 // the box's overhang, as a share of the disc
const LIGHT_R = '24%' // the blob's zero-alpha radius, of that box
const LIGHT_STOPS: [number, number][] = [
    [0, 1],
    [0.2, 0.72],
    [0.4, 0.44],
    [0.6, 0.22],
    [0.8, 0.07],
    [1, 0]
]

const lightBlob = (rgba: string, peak: number): string =>
    `radial-gradient(ellipse ${LIGHT_R} ${LIGHT_R} at 50% 50%, ` +
    LIGHT_STOPS.map(([q, a]) => `${rgba}${(peak * a).toFixed(3)}) ${Math.round(q * 100)}%`).join(', ') +
    ')'

/* ── the floating nav's clearance ─────────────────────────────────────────
 * The section keeps the top 58px clear for a floating pill nav, and everything
 * here lives under that line. */
const NAV_CLEAR = 58
const HEAD_GAP = 16

/* ── the headline ─────────────────────────────────────────────────────────
 * Set to FILL the width on a single line, at every viewport — measured, then
 * solved. A `clamp()` cannot do this: it interpolates against the VIEWPORT, not
 * against the string, so it can only ever be small enough for the longest case
 * or allowed to wrap. */
const HEAD_PROBE = 100 // the size the line is measured at
const HEAD_FILL = 0.9 // share of the space between the margins it fills
const HEAD_MAX_H = 0.3 // …but never taller than this share of the viewport
const HEAD_CY = 0.55 // its optical centre sits here, in viewport heights
const HEAD_WEIGHT = 700 // the display line's weight

/* ── the floor drifts ─────────────────────────────────────────────────────
 * The lattice slides toward the horizon — the ground flowing away, which gives
 * a held frame a direction to sit still in.
 *
 * The floor is CURVED — it lies on a swell rather than flat, so the rows bow
 * and the rays splay. A CSS plane cannot do this: `rotateX` is affine, and an
 * affine map takes straight lines to straight lines, whatever the perspective.
 * So the lattice is drawn as SVG paths over a normalised 0–100 box, sampled
 * along real curves.
 *
 * The model is one surface in two lines. Depth is carried by `s = z0 / z`, the
 * perspective foreshortening — 1 at the near edge, → 0 at the horizon:
 *
 *     x(u,s) = 50 + u·HALF·s
 *     y(u,s) = HORIZON + (BOTTOM − HORIZON)·s + BOW·u²·s^BOW_P
 *
 * `BOW·u²` is the swell: a row's ends drop away from its middle. `BOW_P` is
 * what bends the RAYS — at BOW_P = 1 both x and y are affine in s, so every ray
 * comes out dead straight however much the rows bow. Above 1 the ray's droop
 * no longer keeps pace with its own recession and the line curves. Below ~1.2
 * that reads as a kink rather than a curve.
 *
 * Drift is a PHASE on the depth index, not a transform: z = z0 + i + p. The
 * rows genuinely recede, so their spacing compresses as they go — a translate
 * would slide near rows and far rows at one speed and the perspective would
 * come apart. When p passes 1 each row inherits its neighbour's place, so the
 * wrap is seamless without a spare cell.
 *
 * Cost is the rows only: the rays never move, so they are built once. */
const GRID_HORIZON = 38 // viewBox y of the vanishing line
const GRID_BOTTOM = 106 // …and of the nearest row, past the frame's edge
const GRID_HALF = 165 // half-width at that nearest row
const GRID_ROWS = 21 // rows in flight — the last one lands exactly on GRID_FAR
/* World spacing between rows. Under 1/z the first few rows eat most of the
 * frame and everything behind them piles into a smear; a tighter world pitch
 * puts more rows in the readable band without touching the perspective. */
const GRID_STEP = 0.45
const GRID_COLS = 17 // rays — ODD, so one runs down the centre
const GRID_BOW = 46 // how far a near row's ends drop
const GRID_BOW_P = 1.55 // …and how that drop falls off with depth
const GRID_SEG = 10 // samples per ROW — rebuilt every redraw, and a parabola needs very few
const GRID_SEG_COL = 22 // …per RAY, which is built once and can afford it
/* Rows are spaced by 1/z, so past a certain depth they land closer together
 * than a hairline is wide and merge into one grey smear across the horizon.
 * They fade out over this window in `s` instead, which also buys the floor
 * some depth. The rays get the same treatment as a gradient, since they are
 * static and cannot be faded per frame. */
const GRID_FAR = 0.1 // s at which a row is fully gone
const GRID_NEAR = 0.26 // …and at which it is fully present
const GRID_RATE = 0.075 // rows per second — slow on purpose
const GRID_HZ = 15 // …and how often the lattice is actually redrawn

/* Rounded to 2dp rather than `toFixed`: this runs for every row of every copy
 * on each redraw, and toFixed allocates a string each call. */
const r2 = (n: number): number => Math.round(n * 100) / 100

/* Where the rays' fade begins and ends, in viewBox y. It is an SVG gradient on
 * the STROKE, not a CSS mask: a CSS mask's percentages resolve against the
 * masked element's own bounding box, and the rays' box starts at the horizon.
 * `gradientUnits="userSpaceOnUse"` is measured in viewBox units, which is what
 * this geometry is written in. */
const RAY_Y0 = r2(GRID_HORIZON + (GRID_BOTTOM - GRID_HORIZON) * GRID_FAR)
const RAY_Y1 = r2(GRID_HORIZON + (GRID_BOTTOM - GRID_HORIZON) * GRID_NEAR)

/* ── the window on the lattice ────────────────────────────────────────────
 * The floor is a measuring aid for the WORD, so it is shown where the word is
 * and nowhere else: an ellipse over the line, falling away on every side.
 *
 * The SVG's own box is cropped to that ellipse's reach rather than left at
 * `inset-0`. The rows change shape on every redraw, so the layer re-rasters —
 * twice, because the lens carries its own copy — and a mask on top of it
 * re-composites too. Both are paid per PIXEL of the element, so the box is
 * cropped to the 58% of the frame it can actually draw in. What pays for this
 * floor far more is throttling the redraw (GRID_HZ, in the frame loop).
 *
 * The mask is therefore quoted in the element's box, not the frame's, which is
 * what MASK_* convert — get this wrong and the ellipse drifts off the word. */
const GRID_TOP = 30 // the SVG's box, in % of the frame
const GRID_H = 58 // …so it spans 30–88%
const MASK_CY = 58 // the ellipse, in FRAME %
const MASK_RY = 27
const MASK_RX = 56
const GRID_MASK =
    `radial-gradient(ellipse ${MASK_RX}% ${r2((MASK_RY / GRID_H) * 100)}% at 50% ${r2(((MASK_CY - GRID_TOP) / GRID_H) * 100)}%, ` +
    '#000 0%, #000 48%, rgba(0,0,0,0.74) 68%, rgba(0,0,0,0.26) 86%, transparent 100%)'

const gX = (u: number, s: number): number => r2(50 + u * GRID_HALF * s)
const gY = (u: number, s: number): number =>
    r2(GRID_HORIZON + (GRID_BOTTOM - GRID_HORIZON) * s + GRID_BOW * u * u * Math.pow(s, GRID_BOW_P))

/** One bowed row, at foreshortening `s`. */
function gridRow(s: number): string {
    let d = ''
    for (let i = 0; i <= GRID_SEG; i++) {
        const u = -1 + (2 * i) / GRID_SEG
        d += `${i ? 'L' : 'M'}${gX(u, s)} ${gY(u, s)}`
    }
    return d
}

/** One splayed ray, at lateral position `u`. Sampled linearly in s, which is
 *  linear in SCREEN space — so the samples land evenly along the drawn curve
 *  instead of piling up near the horizon where it is already compressed. */
function gridCol(u: number): string {
    const sMin = GRID_FAR
    let d = ''
    for (let i = 0; i <= GRID_SEG_COL; i++) {
        const s = sMin + (1 - sMin) * (i / GRID_SEG_COL)
        d += `${i ? 'L' : 'M'}${gX(u, s)} ${gY(u, s)}`
    }
    return d
}

/* ── the tide ─────────────────────────────────────────────────────────────
 * The coloured floor RISES toward the type and settles back — a slow swell
 * that carries the colour up into the section rather than leaving it pooled at
 * the bottom edge.
 *
 * It oscillates rather than drifting one way. The lattice can drift forever
 * because it REPEATS; a gradient has no period, so a one-way drift would
 * either run out of colour or need the whole ramp rebuilt every frame. A swell
 * is the honest seamless motion.
 *
 * Like the lattice it is a TRANSFORM, not an animated gradient: re-writing the
 * colour stops every frame repaints a viewport-sized layer, twice over. The
 * element is extended past the bottom edge by exactly the travel, so lifting it
 * can never expose the ground beneath. */
const TIDE = 0.1 // the swell, as a share of viewport height
const TIDE_W = 0.055 // …and its rate — a full breath takes ~18s

/* ── the word writes itself in ────────────────────────────────────────────
 * The house entrance: scale + blur + y, staggered, power4.out. */
const REVEAL_AT = 0.18 // s — after the room has begun to arrive
const REVEAL_STEP = 0.05 // s between letters
const REVEAL_DUR = 0.95 // s each

/* Clearance against the turning disc. The TEST pad is deliberately tiny: it
 * must fire on a real overlap and not on a near miss, because the action it
 * triggers moves the composition. */
const CLEAR_TEST = 4 // px — how close counts as touching
const CLEAR_GAP = 12 // px — the gap left when the line does move

/* ── the bead ─────────────────────────────────────────────────────────────
 * Sized off the SMALLER viewport dimension, not the width: a phone on its side
 * is 844 wide and 390 tall, and a width-derived bead there is a lid over the
 * whole frame. */
const BLOB_R = 0.13 // base radius, as a share of min(vw, vh) …
const BLOB_MIN = 58 // … clamped, in px …
const BLOB_MAX = 168
/* …and then capped against the WIDTH, which wins even over the floor. On a
 * narrow screen the floor alone stopped the bead scaling at all, and at 280px
 * the disc swallowed most of the word. The proportion has to hold, so the cap
 * is applied after the clamp rather than inside it. */
const BLOB_W_MAX = 0.16 // × viewport width
const BLOB_BOX = 2.9 // the element's box, in radii — room for the outline to move

/* ── the LIQUID form ──────────────────────────────────────────────────────
 * Not a circle, and never the same shape twice. Two things are happening:
 *
 *   WOBBLE — an eight-value `border-radius`, every corner breathing on its own
 *   incommensurate period. This is deliberately NOT a clip-path: `overflow`,
 *   the rim's inset shadow and the bead's cast shadow all follow a
 *   border-radius automatically, so the outline, the clip, the lit edge and the
 *   shadow stay welded together for free.
 *
 *   STRETCH — the drop elongates along its heading when it moves and relaxes
 *   back to round when it settles, which is what actually reads as LIQUID: mass
 *   resisting a change of direction. Applied as rotate(θ)·scale·rotate(−θ), a
 *   pure scale along an arbitrary axis, so nothing shears and the content's
 *   orientation is untouched.
 *
 * Both stay gentle on purpose. The displacement map is baked for a circle, so a
 * violent outline would pull the refraction's rim away from the shape's rim. */
const WOBBLE = 11 // % of the radius each corner swings
const WOBBLE_W = [0.37, 0.29, 0.44, 0.33, 0.41, 0.26, 0.31, 0.47]
const STRETCH_PER_SPEED = 0.00019 // per px/s …
const STRETCH_MAX = 0.3 // … capped here
const STRETCH_LAG = 0.004 // and eased, so it swells and relaxes
const BLOB_BREATHE = 0.022

/* The scene copy's magnification. The map bends what it is given; the copy is
 * what makes the letters BIGGER inside the disc, which a slab profile cannot do
 * on its own. A CSS scale cuts the image at the rim, and that cut only reads as
 * a glass edge because the refraction lip is wide enough to hide it. */
const BLOB_MAG = 1.32
/* Real glass is never perfectly spherical, and a uniform scale reads as a zoom
 * rather than as a lens, so the magnification breathes anisotropically. Kept
 * SMALL: any more de-registers the rim and the word reads as twitching. */
const BLOB_ANISO = 0.03
const BLOB_ANISO_W = 0.23

/* Where the lights sit: masked to the outer band, so they read as colour
 * splitting at the rim rather than as paint over the middle. */
const BAND_MASK =
    'radial-gradient(circle at 50% 50%, transparent 14%, rgba(0,0,0,0.22) 34%, rgba(0,0,0,0.55) 54%, rgba(0,0,0,0.85) 74%, #000 90%, #000 100%)'

/* The rim, as inset box-shadows — on their OWN element, painted last.
 *
 * An inset box-shadow paints above its element's background and BELOW its
 * children. On the disc itself the rim would be covered completely by the
 * disc's first child, the scene copy, which is opaque.
 *
 * Bevel geometry, in paint order within the one element: a hairline all round,
 * a bright top edge (light catching the crown), and a dimmer bottom edge — that
 * bottom bounce is the cue that reads as THICKNESS rather than as a stroke. */
const RIM_SHADOW =
    'inset 0 0 0 1px rgba(255,255,255,0.55), inset 0 2px 1.5px -1px rgba(255,255,255,0.7), ' +
    'inset 0 -2px 1.5px -1px rgba(255,255,255,0.3), inset 0 0 10px -3px rgba(24,48,32,0.45)'

const BLOB_LAG = 0.0024 // follow weight — glass has mass

/* ── the key light ────────────────────────────────────────────────────────
 * ONE lamp, fixed in the frame and ABOVE it. It never moves; the bead moves
 * under it, so the glint slides round the circumference as the bead crosses the
 * page — which is the cue the eye reads as CURVATURE. A highlight pinned at a
 * fixed percentage of the disc is identical wherever the bead is, and reads as
 * a printed outline rather than as a lit surface.
 *
 * The lamp is OUTSIDE the viewport on purpose. Put it inside and the bead can
 * pass through it, at which point the aim inverts through 180° over a few
 * pixels and the whole rim snaps. */
const KEY_X = 0.22 // the lamp, in viewport units
const KEY_Y = -0.35

/* ── the optics ───────────────────────────────────────────────────────────
 * The bead refracts with the liquid-glass engine (`@/lib/liquid-glass`): it
 * models the disc as a slab whose surface curves down to the backdrop across a
 * bezel band, solves Snell at that surface for every pixel, and bakes the
 * offsets into a displacement map. Three passes at slightly spread scales give
 * chromatic fringing exactly where light bends.
 *
 * The map is applied as an ORDINARY `filter` to a magnified copy of the scene,
 * not as a backdrop filter. The copy supplies the magnification a slab cannot —
 * the letters are genuinely bigger inside the disc — and the map then bends and
 * fringes them, hardest at the rim, which is the liquid-glass signature. An
 * ordinary `filter: url(#…)` is supported everywhere, so there is no browser
 * gate and no second code path to keep alive. */
/* Bezel is the width of the band the surface curves across, and a WIDER band
 * is a SOFTER one: spreading the same descent over more of the radius makes the
 * gradient gentle everywhere and leaves no ring where the world visibly
 * squeezes. */
const GLASS_BEZEL = 0.32 // × diameter
/* Thickness over bezel IS the surface slope, and it is the real lever. */
const GLASS_THICK = 0.6 // × diameter — glass depth at full engage
/* The colour split is the one cue that cannot be faked with blur, but over
 * near-black ink on a pale ground a wide spread produces pure cyan and orange
 * slivers on the hard letter edges. The fringe belongs BELOW the level where
 * the primaries separate. */
const GLASS_CHROMA = 1.5 // dispersion, ×5% of scale per unit
const GLASS_STEPS = 14 // engage is quantised into this many baked maps
/* The sub-pixel blur is not frost. `feDisplacementMap` quantises the map to
 * maxMag/127 px per step, so the larger the displacement the coarser the
 * stair-stepping on a hard letter edge; half a pixel dissolves it. */
const GLASS_FROST = 'blur(0.5px) saturate(1.26) brightness(1.03) contrast(1.06)'
/* The optics never switch fully off. At a hard zero the disc is optically inert
 * — a flat tinted circle — and it spends most of its life there, since the bead
 * is only over the letters some of the time. A floor keeps the grid and the
 * floor gradient bending under it always. */
const ENGAGE_FLOOR = 0.4

/* ── the glass ENGAGES on the type ────────────────────────────────────────
 * Away from the words the bead is quieter glass. Bring it into the letters and
 * it becomes a full lens — the magnification, the rim compression and the
 * dispersion all come up together.
 *
 * Driven by DISTANCE to the line's ink box, not by a boolean, so it swells in
 * as the bead approaches rather than snapping on at an invisible boundary. */
const ENGAGE_FADE = 0.8 // fades in over this many bead radii
const ENGAGE_LAG = 0.004 // …with its own weight, slower than the follow
const DRIFT_X = 0.17 // the idle wander, in viewport units
const DRIFT_Y = 0.055
const DRIFT_WX = 0.13 // …and its periods
const DRIFT_WY = 0.19

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v)

/* A border-only CONIC gradient, indexed by the angle round the circumference —
 * which is what a rim highlight physically is.
 *
 * A LINEAR gradient can only lay its bright band across a DIAMETER: it lights
 * two opposite arcs equally and can never be dark on the far side. A conic can,
 * and a rim that is bright toward the light and dark away from it is the cue.
 *
 * NO `mix-blend-mode`: `screen` cannot darken at all, so the dark half would be
 * a no-op by construction. Plain alpha compositing goes both ways.
 *
 * `padding` plus the two-layer mask with `composite: exclude` makes this a BAND
 * with thickness rather than a hairline. BOTH spellings are required and in
 * this order: WebKit reads `-webkit-mask-composite: xor`, everyone else reads
 * `mask-composite: exclude`. If the composite ever fails the ring degrades to a
 * filled disc. */
function rimRing(px: number, lit: string, mid: string, far: string): CSSProperties {
    return {
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        pointerEvents: 'none',
        padding: `${px}px`,
        WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        background:
            `conic-gradient(from calc(var(--lpe-rim, -30) * 1deg), ${lit} 0deg, ${mid} 26deg, ` +
            `${mid} 90deg, ${far} 180deg, ${mid} 270deg, ${mid} 334deg, ${lit} 360deg)`
    }
}

/* the frame's margin, as CSS and as the same number in JS */
const MARGIN_CSS = 'clamp(18px,4.2vw,56px)'
const marginPx = (vw: number): number => clamp(vw * 0.042, 18, 56)

/* TWO families: a wide display face for the line and a sans for everything
 * small. The chrome is 8–11px labels, and the display face is all character
 * and no legibility at that size. */
const FACE: CSSProperties = {
    fontFamily: 'var(--font-app-sans, "Helvetica Neue"), Helvetica, Arial, sans-serif'
}
const DISPLAY: CSSProperties = {
    fontFamily: 'var(--font-app-heading, "Helvetica Neue"), Helvetica, Arial, sans-serif'
}

interface LoupeLayout {
    vw: number
    vh: number
    compact: boolean
    barTop: number
    margin: number
    blobR: number
    blobBox: number
}

function layoutFor(vw: number, vh: number): LoupeLayout {
    const blobR = Math.min(clamp(Math.min(vw, vh) * BLOB_R, BLOB_MIN, BLOB_MAX), vw * BLOB_W_MAX)
    return {
        vw,
        vh,
        compact: vw < 900,
        // On a wide screen the masthead sits at the very top and a floating
        // nav rides over the middle of it — they cannot collide, because the
        // masthead's items are pinned to the two edges. On a narrow one the
        // nav spans nearly the whole width, so the masthead drops below it.
        barTop: vw >= 1024 ? 26 : NAV_CLEAR + 14,
        margin: marginPx(vw),
        blobR,
        blobBox: Math.round(blobR * BLOB_BOX)
    }
}

/* the live frame — only ever called from effects and event handlers */
const measureLayout = (): LoupeLayout => layoutFor(window.innerWidth, window.innerHeight)

/* a stand-in until the first layout effect measures the real frame */
const INITIAL_LAYOUT = layoutFor(1440, 900)

interface LoupeProps {
    className?: string
}

const Loupe: FC<LoupeProps> = ({ className }): ReactNode => {
    const [theme, setTheme] = useState<Theme>('light')

    /* Follow the site's theme control live. Nothing in the frame loop reads a
     * colour, so a swap is just a re-render — the loop keeps running untouched. */
    useEffect(() => {
        setTheme(readTheme())
        const mo = new MutationObserver(() => setTheme(readTheme()))
        mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
        return () => mo.disconnect()
    }, [])

    const rootRef = useRef<HTMLElement | null>(null)
    /* one head ref per copy of the scene: [frame, lens, spare] */
    const headRefs = useRef<(HTMLDivElement | null)[]>([null, null, null])
    const beadRef = useRef<HTMLDivElement | null>(null)
    const clipRef = useRef<HTMLDivElement | null>(null)
    const lensRef = useRef<HTMLDivElement | null>(null)
    const glassRef = useRef<HTMLDivElement | null>(null)
    const filterRefs = useRef<GlassFilterRefs>({ filter: null, feImage: null })
    const glassStep = useRef(-1)
    const lightsRef = useRef<HTMLDivElement | null>(null)
    const surfaceRef = useRef<HTMLDivElement | null>(null)
    const greenRef = useRef<HTMLDivElement | null>(null)
    const pinkRef = useRef<HTMLDivElement | null>(null)
    const amberRef = useRef<HTMLDivElement | null>(null)
    const rimRef = useRef<HTMLDivElement | null>(null)
    const chromeRefs = useRef<(HTMLDivElement | null)[]>([null, null, null])
    /* HTMLElement, not HTMLDivElement: the disc is a <button> (it is a real
     * control, not an ornament). Everything applySizes does to it — offsetLeft,
     * offsetTop, offsetWidth, offsetHeight, style.display — is on HTMLElement. */
    const discRefs = useRef<(HTMLElement | null)[]>([null, null, null])
    const footRefs = useRef<(HTMLDivElement | null)[]>([null, null, null])
    /* one entry per copy, each a row of <path>s — the rays are static */
    const rowRefs = useRef<(SVGPathElement | null)[][]>([[], [], []])
    const bandRefs = useRef<(HTMLDivElement | null)[]>([null, null, null])
    /* The word, unsplit and never painted — it carries the TEXT METRICS the
     * placement solve reads. A range over split inline-blocks returns their
     * line boxes rather than the glyphs' ascent and descent, and those boxes
     * move the moment the reveal transforms them. */
    const measureRefs = useRef<(HTMLSpanElement | null)[]>([null, null, null])
    const letterRefs = useRef<(HTMLSpanElement | null)[][]>([[], [], []])

    const layoutRef = useRef<LoupeLayout>(INITIAL_LAYOUT)
    const reducedRef = useRef(false)
    /* where the bead is, where it is heading, and whether a cursor is driving it */
    const bead = useRef({
        x: 0,
        y: 0,
        tx: 0,
        ty: 0,
        seen: false,
        t: 0,
        eng: 0,
        px: 0, // last frame's position, for velocity
        py: 0,
        vx: 0, // …smoothed, so the heading does not jitter at rest
        vy: 0,
        st: 0, // the eased stretch amount
        rim: 999 // the last aim written, so it is written only on change
    })
    /* the line's ink box, in viewport coordinates — what the glass engages on */
    const inkRef = useRef({ l: 0, t: 0, r: 0, b: 0 })

    /* ── sizes — imperative, because they change only on resize ──────────── */
    const applySizes = useCallback((): void => {
        const L = layoutRef.current

        // The lens holds a viewport-sized copy of the scene; it is offset and
        // scaled per frame, never re-laid-out.
        const lens = lensRef.current
        if (lens) {
            lens.style.width = `${L.vw}px`
            lens.style.height = `${L.vh}px`
        }
        const bd = beadRef.current
        if (bd) {
            bd.style.width = `${L.blobBox}px`
            bd.style.height = `${L.blobBox}px`
        }
        // The disc must be EXACTLY the glass circle: the filter region is the
        // element's own border box, so a box the size of the outer bead would
        // put the map's rim nowhere near the bead's rim.
        const dc = clipRef.current
        if (dc) {
            const d = L.blobR * 2
            const off = L.blobBox / 2 - L.blobR
            dc.style.width = `${d}px`
            dc.style.height = `${d}px`
            dc.style.left = `${off}px`
            dc.style.top = `${off}px`
        }
        glassStep.current = -1 // the map is sized to the disc; re-bake it

        const head = headRefs.current[0]
        if (!head) return

        // Solve the headline's size from its own measured width. Two reflows
        // on resize only; never per frame.
        const target = (L.vw - L.margin * 2) * HEAD_FILL
        head.style.fontSize = `${HEAD_PROBE}px`
        const natural = head.scrollWidth
        if (natural > 0) {
            head.style.fontSize = `${Math.min((HEAD_PROBE * target) / natural, L.vh * HEAD_MAX_H)}px`
        }

        // Place it by its INK, not by its box. At this leading the ascenders
        // overshoot the box, so a box-aligned headline sits optically low and
        // can run up into the nav. Box and ink are under the same transform, so
        // their difference is the overshoot alone.
        const box = head.getBoundingClientRect()
        const ruler = measureRefs.current[0] ?? head
        const range = document.createRange()
        range.selectNodeContents(ruler)
        const ink = range.getBoundingClientRect()

        if (ink.height > 0) {
            const overshoot = box.top - ink.top
            const wanted = L.vh * HEAD_CY - ink.height / 2
            const floor = Math.max(NAV_CLEAR + HEAD_GAP, L.barTop + 40)
            let top = Math.max(wanted, floor)
            // Centred by its INK as well, not by its box. Side bearings differ
            // letter to letter, so centring the box leaves the word visibly
            // off-centre; centring the ink does not.
            const left = (L.vw - ink.width) / 2

            /* ── the line must clear the turning disc ─────────────────────
             * On a SHORT LANDSCAPE frame the line's band and the top-right
             * chrome stack are the same band, and the disc lands on the last
             * letters. Everywhere else they miss each other vertically, so this
             * is driven by the ACTUAL overlap rather than by a breakpoint.
             *
             * Measure the disc's LAYOUT box, not `getBoundingClientRect`: the
             * disc turns, and a rotating square's bounding box breathes by √2
             * as it goes round, so the rect would report a collision that is
             * not there — and report it intermittently, which is worse. */
            const discs = discRefs.current
            const d0 = discs[0]
            for (const d of discs) if (d) d.style.display = ''
            if (d0) {
                const dl = d0.offsetLeft
                const dt = d0.offsetTop
                const dr = dl + d0.offsetWidth
                const db = dt + d0.offsetHeight
                const hit = (y: number): boolean =>
                    left + ink.width > dl - CLEAR_TEST &&
                    left < dr + CLEAR_TEST &&
                    y + ink.height > dt - CLEAR_TEST &&
                    y < db + CLEAR_TEST

                if (hit(top)) {
                    const footEl = footRefs.current[0]
                    const footTop = footEl ? footEl.offsetTop : L.vh
                    const below = db + CLEAR_GAP
                    if (below + ink.height <= footTop - CLEAR_GAP) {
                        // there is room under it: the line drops below the disc
                        top = below
                    } else {
                        // there is not: the ORNAMENT yields to the content
                        for (const d of discs) if (d) d.style.display = 'none'
                    }
                }
            }

            head.style.top = `${top + overshoot}px`
            head.style.left = `${left - (ink.left - box.left)}px`
        }

        // Re-measure AFTER the move: the engage test is against where the ink
        // actually ended up, not where it was when its size was measured.
        {
            const after = document.createRange()
            after.selectNodeContents(measureRefs.current[0] ?? head)
            const k = after.getBoundingClientRect()
            inkRef.current = { l: k.left, t: k.top, r: k.right, b: k.bottom }
        }

        // every copy wears whatever the real one solved
        for (const c of headRefs.current.slice(1)) {
            if (!c) continue
            c.style.fontSize = head.style.fontSize
            c.style.top = head.style.top
            c.style.left = head.style.left
        }
    }, [])

    useLayoutEffect(() => {
        layoutRef.current = measureLayout()
        applySizes()
        // The headline is measured, so it must be re-solved once the display
        // face has actually loaded — measuring against the fallback gives a
        // line of the wrong width and the error is permanent.
        let alive = true
        document.fonts?.ready.then(() => {
            if (alive) applySizes()
        })
        return () => {
            alive = false
        }
    }, [applySizes])

    /* ── the clock ─────────────────────────────────────────────────────────── */
    useEffect(() => {
        reducedRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const L0 = layoutRef.current
        const B = bead.current
        B.x = B.tx = L0.vw * 0.5
        B.y = B.ty = L0.vh * HEAD_CY

        let raf = 0
        let last = performance.now()
        let visible = true
        let gridAt = -1 // clock of the last lattice redraw

        const onResize = (): void => {
            layoutRef.current = measureLayout()
            applySizes()
        }
        window.addEventListener('resize', onResize)

        /* The bead — and ONLY the bead — follows the cursor. Nothing else in
         * the frame moves with the pointer: the type is something you read,
         * and type that drifts under the pointer is type you have to chase.
         * What moves is the glass you are holding over it. */
        const onMove = (e: PointerEvent): void => {
            if (e.pointerType === 'touch') return
            B.tx = e.clientX
            B.ty = e.clientY
            if (!B.seen) {
                B.seen = true
                B.x = e.clientX
                B.y = e.clientY
            }
        }
        const onLeave = (): void => {
            B.seen = false
        }
        window.addEventListener('pointermove', onMove)
        document.addEventListener('pointerleave', onLeave)

        const io = new IntersectionObserver(
            ([en]) => {
                visible = en.isIntersecting
            },
            { threshold: 0.02 }
        )
        if (rootRef.current) io.observe(rootRef.current)
        const onVis = (): void => {
            last = performance.now()
        }
        document.addEventListener('visibilitychange', onVis)

        /* The entrance: the room arrives, the word writes itself in, and the
         * glass forms on top of it.
         *
         * The letters are tweened PER COPY rather than as one flat array: the
         * lens holds its own set, and a single stagger across both would deal
         * the delays out in DOM order — so the word inside the bead would lag
         * the word outside it by the whole reveal. Two tweens built in the same
         * tick share the ticker and stay identical. */
        const reveal = (done: boolean): void => {
            for (const letters of letterRefs.current) {
                const els = letters.filter(Boolean) as HTMLElement[]
                if (!els.length) continue
                if (done) {
                    for (const el of els) el.style.opacity = '1'
                    continue
                }
                gsap.fromTo(
                    els,
                    { opacity: 0, yPercent: 26, scale: 0.94, filter: 'blur(9px)' },
                    {
                        opacity: 1,
                        yPercent: 0,
                        scale: 1,
                        filter: 'blur(0px)',
                        duration: REVEAL_DUR,
                        ease: 'power4.out',
                        delay: REVEAL_AT,
                        stagger: REVEAL_STEP,
                        clearProps: 'filter'
                    }
                )
            }
        }

        if (reducedRef.current) {
            for (const c of chromeRefs.current) if (c) c.style.opacity = '1'
            if (beadRef.current) beadRef.current.style.opacity = '1'
            reveal(true)
        } else {
            reveal(false)
            const chromeEls = chromeRefs.current.filter(Boolean) as HTMLElement[]
            if (chromeEls.length) {
                gsap.fromTo(
                    chromeEls,
                    { opacity: 0, y: 26, filter: 'blur(14px)' },
                    { opacity: 1, y: 0, filter: 'blur(0px)', duration: 1.15, ease: 'power4.out' }
                )
            }
            if (beadRef.current) {
                gsap.fromTo(
                    beadRef.current,
                    { opacity: 0 },
                    { opacity: 1, duration: 1.1, ease: 'power3.out', delay: 0.3 }
                )
            }
        }

        const pts = new Array<string>(8) // the eight border-radius terms

        const frame = (now: number): void => {
            raf = requestAnimationFrame(frame)
            const dt = Math.min(0.05, (now - last) / 1000)
            last = now
            if (!visible || document.hidden) return
            const L = layoutRef.current
            const reduced = reducedRef.current
            if (!reduced) B.t += dt
            const t = B.t

            /* Where it wants to be: the cursor, or — with no cursor — a slow
             * wander of its own. It wanders around the LINE's measured centre,
             * not around HEAD_CY: on a short landscape frame the line drops to
             * clear the turning disc, and a bead orbiting the nominal centre
             * would spend its time over empty ground. */
            if (!B.seen) {
                const K = inkRef.current
                const cy = K.b > K.t ? (K.t + K.b) / 2 : L.vh * HEAD_CY
                B.tx = L.vw * 0.5 + L.vw * DRIFT_X * Math.sin(t * DRIFT_WX * Math.PI)
                B.ty = cy + L.vh * DRIFT_Y * Math.sin(t * DRIFT_WY * Math.PI + 1.1)
            }
            const f = reduced ? 1 : 1 - Math.pow(BLOB_LAG, dt)
            B.x += (B.tx - B.x) * f
            B.y += (B.ty - B.y) * f

            const R = L.blobR
            const half = L.blobBox / 2

            /* how far into the letters is it? 0 outside, 1 with its centre in the ink */
            const K = inkRef.current
            const dx = Math.max(K.l - B.x, 0, B.x - K.r)
            const dy = Math.max(K.t - B.y, 0, B.y - K.b)
            const dist = Math.hypot(dx, dy)
            const engTarget = clamp(1 - dist / (R * ENGAGE_FADE), 0, 1)
            B.eng += (engTarget - B.eng) * (reduced ? 1 : 1 - Math.pow(ENGAGE_LAG, dt))
            const eng = B.eng

            /* ── the liquid form ───────────────────────────────────────────
             * The outline wobbles on eight independent clocks, and the whole
             * drop elongates along the direction it is travelling. */
            const clipEl = clipRef.current
            if (clipEl) {
                if (reduced) {
                    clipEl.style.borderRadius = '50%'
                    clipEl.style.transform = ''
                } else {
                    for (let i = 0; i < 8; i++) {
                        pts[i] = `${(50 + WOBBLE * Math.sin(t * WOBBLE_W[i] * Math.PI + i * 1.7)).toFixed(2)}%`
                    }
                    clipEl.style.borderRadius = `${pts[0]} ${pts[1]} ${pts[2]} ${pts[3]} / ${pts[4]} ${pts[5]} ${pts[6]} ${pts[7]}`

                    // velocity, smoothed — an unsmoothed heading spins wildly at
                    // rest, where the velocity is numerical noise, not a direction
                    const kv = 1 - Math.pow(0.02, dt)
                    B.vx += ((B.x - B.px) / Math.max(dt, 0.001) - B.vx) * kv
                    B.vy += ((B.y - B.py) / Math.max(dt, 0.001) - B.vy) * kv
                    const speed = Math.hypot(B.vx, B.vy)
                    const target = clamp(speed * STRETCH_PER_SPEED, 0, STRETCH_MAX)
                    B.st += (target - B.st) * (1 - Math.pow(STRETCH_LAG, dt))
                    const breathe = 1 + BLOB_BREATHE * Math.sin(t * 0.37)
                    const deg = (Math.atan2(B.vy, B.vx) * 180) / Math.PI
                    clipEl.style.transform =
                        `rotate(${deg.toFixed(2)}deg) scale(${(breathe * (1 + B.st)).toFixed(4)}, ${(breathe * (1 - B.st * 0.62)).toFixed(4)}) rotate(${(-deg).toFixed(2)}deg)`
                }
            }
            B.px = B.x
            B.py = B.y

            /* Aim the rim at the lamp. One atan2 and, at most, one style write
             * per frame — quantised to a half degree, which is finer than the
             * ring can show and coarse enough that a settled bead stops writing
             * entirely. */
            const cl = clipRef.current
            if (cl) {
                const aim = Math.round(
                    ((Math.atan2(L.vh * KEY_Y - B.y, L.vw * KEY_X - B.x) * 180) / Math.PI + 90) * 2
                ) / 2
                if (aim !== B.rim) {
                    cl.style.setProperty('--lpe-rim', String(aim))
                    B.rim = aim
                }
            }

            /* The bead's box, and the copy of the scene inside it. A world point
             * P must land at (half, half) + k·(P − O) inside the box, so the copy
             * carries transform-origin O — the bead's centre in SCREEN
             * coordinates — and translates by −(box top-left). */
            const beadEl = beadRef.current
            if (beadEl) {
                beadEl.style.transform = `translate3d(${(B.x - half).toFixed(2)}px, ${(B.y - half).toFixed(2)}px, 0)`
            }

            /* The OPTICS ride a floored engage — glass is glass even off the
             * type, and there is always the grid and the floor gradient to bend.
             * The PAINTED layers keep the raw `eng`, because those genuinely
             * should stay quiet until there is something worth lighting. */
            const opt = ENGAGE_FLOOR + (1 - ENGAGE_FLOOR) * eng
            const an = BLOB_ANISO * Math.sin(t * BLOB_ANISO_W) * opt
            /* REGISTRATION. The copy's layout origin is the DISC's origin —
             * world (B.x − R, B.y − R) — because the lens sits inside the glass
             * inside the disc, and applySizes pins the disc at blobBox/2 − blobR.
             * It is NOT the bead box's origin: using that would show a different
             * piece of the page from the one the disc is sitting on. */
            const off = `translate3d(${(R - B.x).toFixed(2)}px, ${(R - B.y).toFixed(2)}px, 0)`
            const org = `${B.x.toFixed(2)}px ${B.y.toFixed(2)}px`

            /* ── the optics ────────────────────────────────────────────────
             * Engage drives the glass's DEPTH: thin off the type, the full
             * thickness on the letters, where the type swells and fringes at
             * the rim.
             *
             * Quantised into GLASS_STEPS: the map is a function of (diameter,
             * thickness) only, so a handful of distinct maps cover the whole
             * range and every one after the first sweep is a cache hit. */
            {
                const step = Math.round(opt * GLASS_STEPS)
                if (step !== glassStep.current) {
                    const d = R * 2
                    const map = makeGlassMap({
                        width: d,
                        height: d,
                        radius: d / 2,
                        bezel: d * GLASS_BEZEL,
                        thickness: Math.max(0.5, (d * GLASS_THICK * step) / GLASS_STEPS)
                    })
                    if (map && applyGlassMap(filterRefs.current, map, (GLASS_CHROMA * step) / GLASS_STEPS)) {
                        glassStep.current = step
                    }
                }
            }

            // Quieter off the type, a full lens on the letters: the
            // magnification rides the same floored engage.
            const lensEl = lensRef.current
            if (lensEl) {
                const m = 1 + (BLOB_MAG - 1) * opt
                lensEl.style.transformOrigin = org
                lensEl.style.transform = `${off} scale(${(m * (1 + an)).toFixed(4)}, ${(m * (1 - an)).toFixed(4)})`
            }
            // With real optics doing the bending, the painted lights are
            // ACCENTS, capped well below 1: at full strength they would switch
            // on exactly when the bending does and paint over it.
            const lw = lightsRef.current
            if (lw) lw.style.opacity = (0.12 + 0.3 * eng).toFixed(3)
            // …and so does the modelling. A full specular on a disc that is
            // bending nothing reads as a frosted pearl sitting on the page.
            const sf = surfaceRef.current
            if (sf) sf.style.opacity = (0.34 + 0.24 * eng).toFixed(3)

            /* The floor drifts toward the horizon. Written to every copy from
             * the one clock, rather than left to a CSS animation per element:
             * two animations that started a frame apart would show a seam of
             * mismatched grid at the bead's edge. With `reduced` the clock does
             * not advance, so this is simply still. The rays are NOT touched:
             * they are fixed in the surface and only the rows travel.
             *
             * And it is THROTTLED. Rewriting a row's `d` re-rasters the whole
             * SVG layer, twice over, and the drift is so slow that a row moves a
             * fraction of a pixel per frame. At GRID_HZ the motion is identical
             * to the eye and the cost is a quarter. */
            const gp = (t * GRID_RATE) % 1
            if (t - gridAt >= 1 / GRID_HZ) {
                gridAt = t
                for (const rows of rowRefs.current) {
                    for (let i = 0; i < rows.length; i++) {
                        const el = rows[i]
                        if (!el) continue
                        const sd = 1 / (1 + (i + gp) * GRID_STEP)
                        const op = Math.max(0, Math.min(1, (sd - GRID_FAR) / (GRID_NEAR - GRID_FAR)))
                        // A row past the fade draws nothing, so it is not worth
                        // a path. Opacity is quantised and written only on change.
                        if (op <= 0) {
                            if (el.style.opacity !== '0') el.style.opacity = '0'
                            continue
                        }
                        el.setAttribute('d', gridRow(sd))
                        const q = (Math.round(op * 50) / 50).toString()
                        if (el.style.opacity !== q) el.style.opacity = q
                    }
                }
            }

            /* The tide: the colour swells up toward the type and settles back.
             * (1 − cos)/2 rather than a raw sine, so it eases at both ends of the
             * breath instead of turning around at full speed. */
            const by = -TIDE * L.vh * (0.5 - 0.5 * Math.cos(t * TIDE_W * Math.PI * 2))
            for (const tide of bandRefs.current) {
                if (tide) tide.style.transform = `translate3d(0, ${by.toFixed(2)}px, 0)`
            }

            /* The lights, orbiting inside the glass on their own periods. Each
             * sits at the centre of its own oversized box, so the offset lives in
             * the orbit and the ramp is never cut. */
            const g = greenRef.current
            const pk = pinkRef.current
            const am = amberRef.current
            if (g) {
                g.style.transform =
                    `translate3d(${(R * (-0.32 + 0.42 * Math.cos(t * 0.41))).toFixed(1)}px, ${(R * (-0.28 + 0.36 * Math.sin(t * 0.53 + 0.8))).toFixed(1)}px, 0)`
            }
            if (pk) {
                pk.style.transform =
                    `translate3d(${(R * (0.32 + 0.46 * Math.cos(t * -0.29 + 2.2))).toFixed(1)}px, ${(R * (0.28 + 0.4 * Math.sin(t * 0.37 + 3.4))).toFixed(1)}px, 0)`
            }
            if (am) {
                am.style.transform =
                    `translate3d(${(R * (0.04 + 0.5 * Math.cos(t * 0.23 + 4.1))).toFixed(1)}px, ${(R * (-0.4 + 0.44 * Math.sin(t * -0.31 + 1.6))).toFixed(1)}px, 0)`
            }
        }
        raf = requestAnimationFrame(frame)

        return () => {
            cancelAnimationFrame(raf)
            io.disconnect()
            window.removeEventListener('resize', onResize)
            window.removeEventListener('pointermove', onMove)
            document.removeEventListener('pointerleave', onLeave)
            document.removeEventListener('visibilitychange', onVis)
        }
    }, [applySizes])

    const {
        field,
        band,
        grid,
        ink,
        sub,
        line,
        edge,
        contact,
        blend,
        contour,
        rimLit,
        rimMid,
        rimFar,
        veil
    } = PALETTE[theme]

    /* ── one scene, rendered twice ──────────────────────────────────────────
     * Once as the frame you look at, once inside the glass. The copy is inert:
     * aria-hidden, out of the tab order, and its SVG ids are suffixed so the
     * copies do not fight over them. */
    const scene = (copy: 0 | 1 | 2): ReactNode => (
        <>
            <div className='absolute inset-0' style={{ background: field }} />

            {/* The measuring floor — bowed, receding, and shown only under the
                word. `preserveAspectRatio: none` lets the 0–100 box stretch to
                the frame; the strokes are held at a true hairline by
                `non-scaling-stroke`, so a very wide frame does not get a fat grid. */}
            <svg
                /* `w-full`, NOT `inset-x-0`. An <svg> is a REPLACED element:
                   with width:auto, `left:0; right:0` does not stretch it — it
                   falls back to its intrinsic ratio and overflows the frame on
                   every portrait viewport. */
                className='pointer-events-none absolute left-0 w-full'
                viewBox={`0 ${GRID_TOP} 100 ${GRID_H}`}
                preserveAspectRatio='none'
                style={{
                    top: `${GRID_TOP}%`,
                    height: `${GRID_H}%`,
                    WebkitMaskImage: GRID_MASK,
                    maskImage: GRID_MASK
                }}
                aria-hidden
            >
                <g fill='none' stroke={grid} strokeWidth={1} vectorEffect='non-scaling-stroke'>
                    {/* the rays: fixed in the surface, so built once — and faded
                        near the horizon by a gradient rather than per frame,
                        because everything converges there. */}
                    <defs>
                        <linearGradient
                            id={`loupe-ray-${copy}`}
                            gradientUnits='userSpaceOnUse'
                            x1='0'
                            y1={RAY_Y0}
                            x2='0'
                            y2={RAY_Y1}
                        >
                            <stop offset='0' stopColor={grid} stopOpacity='0' />
                            <stop offset='1' stopColor={grid} stopOpacity='1' />
                        </linearGradient>
                    </defs>

                    <g stroke={`url(#loupe-ray-${copy})`}>
                        {Array.from({ length: GRID_COLS }, (_, i) => (
                            <path
                                key={`c${i}`}
                                d={gridCol(-1 + (2 * i) / (GRID_COLS - 1))}
                                vectorEffect='non-scaling-stroke'
                            />
                        ))}
                    </g>

                    {/* the rows: the only thing that travels. `d` is written by
                        the frame loop; the value here is just the first paint. */}
                    {Array.from({ length: GRID_ROWS }, (_, i) => (
                        <path
                            key={`r${i}`}
                            ref={(el) => {
                                rowRefs.current[copy][i] = el
                            }}
                            d={gridRow(1 / (1 + i * GRID_STEP))}
                            vectorEffect='non-scaling-stroke'
                            style={{ opacity: 0 }}
                        />
                    ))}
                </g>
            </svg>

            {/* …and the veil that takes the corners. The mask above fades the
                lattice out; this lays the ground colour back OVER the fade, so
                the corners are the field itself rather than a thinned grid. */}
            <div className='pointer-events-none absolute inset-0' style={{ background: veil }} />

            {/* The floor deepens into colour, and swells toward the type. The
                clipping wrapper is load-bearing: the band hangs TIDE past the
                bottom edge so lifting it cannot expose bare ground, and without
                a clip that overhang counts toward the section's scroll box. */}
            <div className='pointer-events-none absolute inset-0 overflow-hidden'>
                <div
                    ref={(el) => {
                        bandRefs.current[copy] = el
                    }}
                    className='absolute inset-x-0 top-0'
                    style={{ bottom: `-${TIDE * 100}%`, background: band }}
                />
            </div>

            {/* The chrome lives INSIDE the scene, so the bead REFRACTS the
                masthead, the control, the turning disc and the credits instead
                of erasing them. */}
            <div
                ref={(el) => {
                    chromeRefs.current[copy] = el
                }}
                /* `pointer-events: none` on the LAYER, re-enabled on the two
                   real controls below. Without it this is an invisible
                   viewport-sized hit target sitting over the whole frame, and it
                   silently swallows every click in the section. */
                className='pointer-events-none absolute inset-0 z-[400]'
                style={{ opacity: 0 }}
            >
                <div
                    className='pointer-events-none absolute top-[72px] flex items-start gap-[clamp(14px,2.4vw,34px)] lg:top-[26px]'
                    style={{ left: MARGIN_CSS }}
                >
                    <div className='text-[11px] font-semibold leading-[1.28] tracking-[-0.01em]' style={{ color: ink }}>
                        {COPY.brand}
                        <div className='text-[8px] font-medium uppercase tracking-[0.22em]' style={{ color: sub }}>
                            {COPY.brandSub}
                        </div>
                    </div>
                    <div
                        className='hidden pl-[clamp(14px,2.4vw,34px)] text-[10px] font-medium leading-[1.5] sm:block'
                        style={{ color: sub, borderLeft: `1px solid ${line}` }}
                    >
                        {COPY.meta.map((m) => (
                            <div key={m}>{m}</div>
                        ))}
                    </div>
                </div>

                {/* the one control */}
                <button
                    type='button'
                    tabIndex={copy > 0 ? -1 : undefined}
                    aria-hidden={copy > 0 || undefined}
                    className='pointer-events-auto absolute top-[72px] inline-flex items-center gap-[7px] rounded-full px-[14px] py-[7px] text-[10px] font-medium outline-none focus-visible:underline lg:top-[26px]'
                    style={{ right: MARGIN_CSS, border: `1px solid ${line}`, color: ink, cursor: 'pointer' }}
                >
                    <span
                        aria-hidden
                        style={{
                            display: 'inline-block',
                            width: 7,
                            height: 7,
                            transform: 'rotate(45deg)',
                            border: `1px solid ${ink}`
                        }}
                    />
                    {COPY.cta}
                </button>

                {/* The disc that turns — a real control, not an ornament. Its
                    rotation is a CSS animation on this element (`.loupe-disc`
                    in globals.css) and a <button> takes a transform exactly as a
                    <div> does; the browser's default button chrome is reset
                    inline. The clearance logic writes `display` to this same
                    ref, which works on a button unchanged. */}
                <button
                    type='button'
                    ref={(el) => {
                        discRefs.current[copy] = el
                    }}
                    tabIndex={copy > 0 ? -1 : undefined}
                    aria-hidden={copy > 0 || undefined}
                    aria-label={COPY.discLabel}
                    className='loupe-disc pointer-events-auto absolute top-[126px] h-[62px] w-[62px] rounded-full outline-none focus-visible:ring-2 lg:top-[80px] lg:h-[86px] lg:w-[86px]'
                    style={{ right: MARGIN_CSS, background: 'none', border: 0, padding: 0, cursor: 'pointer', color: ink }}
                >
                    <svg viewBox='0 0 100 100' className='h-full w-full' aria-hidden>
                        <defs>
                            <path
                                id={`loupe-disc-path-${copy}`}
                                d='M50,50 m-34,0 a34,34 0 1,1 68,0 a34,34 0 1,1 -68,0'
                                fill='none'
                            />
                        </defs>
                        <circle cx='50' cy='50' r='49' fill='none' stroke={line} />
                        <circle cx='50' cy='50' r='4.5' fill={ink} />
                        <text style={{ ...FACE, fontSize: 9.4, fontWeight: 600, letterSpacing: '0.12em' }} fill={sub}>
                            <textPath href={`#loupe-disc-path-${copy}`}>{COPY.disc}</textPath>
                        </text>
                    </svg>
                </button>

                {/* footer */}
                <div
                    ref={(el) => {
                        footRefs.current[copy] = el
                    }}
                    className='pointer-events-none absolute flex items-end justify-between text-[10px] font-semibold uppercase tracking-[0.16em]'
                    style={{ left: MARGIN_CSS, right: MARGIN_CSS, bottom: 'clamp(18px,4.5vh,42px)', color: ink }}
                >
                    <span className='whitespace-nowrap'>{COPY.footLeft}</span>
                    <span className='flex items-center gap-[8px] whitespace-nowrap'>
                        {/* The connective label is the first thing to go on a
                            very narrow frame. The NAME carries the meaning. */}
                        <span
                            className='hidden font-medium normal-case tracking-normal min-[300px]:inline'
                            style={{ color: sub }}
                        >
                            {COPY.footRight}
                        </span>
                        {COPY.footRightName}
                    </span>
                </div>
            </div>

            {/* THE LINE. `aria-hidden` on every copy, including the real one:
                the word is split into per-letter spans and a screen reader would
                spell it out. The section's own `aria-label` already carries it. */}
            <div
                ref={(el) => {
                    headRefs.current[copy] = el
                }}
                aria-hidden
                /* The display face is WIDE and already generously spaced; it
                   takes negative tracking well, but past about -0.03em its
                   splayed letters start to collide. */
                className='pointer-events-none absolute whitespace-nowrap leading-[0.86] tracking-[-0.03em]'
                style={{ ...DISPLAY, fontWeight: HEAD_WEIGHT, color: ink, fontSize: 64, top: 200, left: 40 }}
            >
                {/* the ruler: unsplit, unpainted, never animated. It sets the
                    head's box — so the fit solve still reads the real rendered
                    width off `scrollWidth` — and it is what the ink is measured from. */}
                <span
                    ref={(el) => {
                        measureRefs.current[copy] = el
                    }}
                    style={{ visibility: 'hidden' }}
                >
                    {COPY.headline}
                </span>
                {/* the painted word, a letter at a time, laid exactly over the ruler */}
                <span className='absolute left-0 top-0' style={{ whiteSpace: 'pre' }}>
                    {[...COPY.headline].map((ch, i) => (
                        <span
                            key={i}
                            ref={(el) => {
                                letterRefs.current[copy][i] = el
                            }}
                            className='inline-block'
                            style={{ opacity: 0 }}
                        >
                            {ch === ' ' ? ' ' : ch}
                        </span>
                    ))}
                </span>
            </div>
        </>
    )

    return (
        <section
            ref={rootRef}
            className={cn('absolute inset-0 overflow-hidden', className)}
            /* `isolation: isolate` is load-bearing, not decoration. This root is
               `position: absolute; z-index: auto`, which does NOT create a
               stacking context — so the chrome's z-400 and the bead's z-600
               would escape into the host page's stacking context and paint over
               its navigation. Isolating keeps 400/600 meaningful inside this
               section and meaningless outside it. */
            style={{ background: field, color: ink, isolation: 'isolate', ...FACE }}
            aria-label={`${COPY.brand} — ${COPY.headline}`}
        >
            {scene(0)}

            {/* The optical filter — a chromatic three-pass chain. Each
                displacement is isolated to one channel and screen-blended back,
                and because the centre of the map is neutral all three passes
                agree there: the fringe appears only where light actually bends.
                `colorInterpolationFilters="sRGB"` is mandatory — without it the
                map's neutral 128 does not decode as neutral and the whole
                backdrop shears sideways. */}
            <svg width='0' height='0' aria-hidden className='absolute'>
                <filter
                    id='loupe-glass'
                    ref={(el) => {
                        filterRefs.current.filter = el
                    }}
                    filterUnits='userSpaceOnUse'
                    colorInterpolationFilters='sRGB'
                >
                    <feImage
                        ref={(el) => {
                            filterRefs.current.feImage = el
                        }}
                        preserveAspectRatio='none'
                        result='map'
                    />
                    <feDisplacementMap
                        ref={(el) => {
                            filterRefs.current.dispR = el
                        }}
                        in='SourceGraphic'
                        in2='map'
                        xChannelSelector='R'
                        yChannelSelector='G'
                        result='dr'
                    />
                    <feColorMatrix in='dr' type='matrix' values='1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0' result='cr' />
                    <feDisplacementMap
                        ref={(el) => {
                            filterRefs.current.dispG = el
                        }}
                        in='SourceGraphic'
                        in2='map'
                        xChannelSelector='R'
                        yChannelSelector='G'
                        result='dg'
                    />
                    <feColorMatrix in='dg' type='matrix' values='0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0' result='cg' />
                    <feDisplacementMap
                        ref={(el) => {
                            filterRefs.current.dispB = el
                        }}
                        in='SourceGraphic'
                        in2='map'
                        xChannelSelector='R'
                        yChannelSelector='G'
                        result='db'
                    />
                    <feColorMatrix in='db' type='matrix' values='0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0' result='cb' />
                    <feBlend in='cr' in2='cg' mode='screen' result='crg' />
                    <feBlend in='crg' in2='cb' mode='screen' />
                </filter>
            </svg>

            {/* ── the bead ────────────────────────────────────────────────────
                A drop of glass that kneads itself and drifts across the type.
                Inside it: the same scene again, magnified about the bead's
                centre and clipped to its outline — so the letters genuinely
                swell and shift. `pointer-events: none`, always: it rides over
                the controls and must never take the click. */}
            <div
                ref={beadRef}
                aria-hidden
                className='pointer-events-none absolute left-0 top-0 z-[600]'
                style={{ opacity: 0, willChange: 'transform' }}
            >
                <div
                    ref={clipRef}
                    /* The cast shadow is a BOX-shadow here, not a drop-shadow on
                       the bead. A box-shadow follows `border-radius` exactly —
                       and the liquid outline IS a border-radius — so it tracks the
                       shape for free, and it is not clipped by the element's own
                       overflow. A glass bead on white paper is outlined DARK, not
                       light, so the 1px contour is a dark hairline. */
                    className='absolute overflow-hidden'
                    style={{ borderRadius: '50%', boxShadow: `0 0 0 1px ${contour}, ${contact}` }}
                >
                    {/* 1 — THE GLASS. A magnified copy of the scene, refracted
                        through the Snell map. The copy supplies the magnification
                        (a slab's interior is flat, so the map alone leaves the
                        middle of the word untouched); the map supplies the
                        bending and the fringe, hardest at the rim. */}
                    <div
                        ref={glassRef}
                        className='absolute inset-0 overflow-hidden'
                        style={{ filter: `url(#loupe-glass) ${GLASS_FROST}` }}
                    >
                        <div ref={lensRef} className='absolute left-0 top-0' style={{ willChange: 'transform' }}>
                            {scene(1)}
                        </div>
                    </div>

                    {/* 2 — dispersion. The lights are masked to the SAME outer
                        band, so they read as colour splitting at the edge of the
                        glass rather than as paint laid over the middle. Keeping
                        the centre clear is what lets the refracted type stay
                        legible — and the refraction is the whole subject. */}
                    <div ref={lightsRef} className='absolute inset-0' style={{ opacity: 0.18 }}>
                        <div
                            className='absolute inset-0 overflow-hidden'
                            style={{ WebkitMaskImage: BAND_MASK, maskImage: BAND_MASK }}
                        >
                            <div
                                ref={greenRef}
                                className='absolute'
                                style={{
                                    inset: `-${LIGHT_PAD * 100}%`,
                                    background: lightBlob(LIGHT_GREEN, 0.7),
                                    mixBlendMode: blend,
                                    willChange: 'transform'
                                }}
                            />
                        </div>
                        <div
                            className='absolute inset-0 overflow-hidden'
                            style={{ WebkitMaskImage: BAND_MASK, maskImage: BAND_MASK }}
                        >
                            <div
                                ref={pinkRef}
                                className='absolute'
                                style={{
                                    inset: `-${LIGHT_PAD * 100}%`,
                                    background: lightBlob(LIGHT_PINK, 0.68),
                                    mixBlendMode: blend,
                                    willChange: 'transform'
                                }}
                            />
                        </div>
                        {/* the warm one, on its own slower orbit — green and pink
                            alone read as a two-tone gel; the amber between them is
                            what makes it look like light split by a prism. */}
                        <div
                            className='absolute inset-0 overflow-hidden'
                            style={{ WebkitMaskImage: BAND_MASK, maskImage: BAND_MASK }}
                        >
                            <div
                                ref={amberRef}
                                className='absolute'
                                style={{
                                    inset: `-${LIGHT_PAD * 100}%`,
                                    background: lightBlob(LIGHT_AMBER, 0.4),
                                    mixBlendMode: blend,
                                    willChange: 'transform'
                                }}
                            />
                        </div>
                    </div>

                    {/* 3 — the surface: a broad shoulder toward the light, a
                        small hot specular on it, and the body shading. */}
                    <div ref={surfaceRef} className='absolute inset-0' style={{ opacity: 0.34 }}>
                        <div
                            className='absolute inset-0'
                            style={{
                                background:
                                    'radial-gradient(circle at 32% 24%, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.05) 12%, rgba(255,255,255,0) 21%)'
                            }}
                        />
                        <div
                            className='absolute inset-0'
                            style={{
                                background:
                                    'radial-gradient(circle at 30% 21%, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.22) 3.5%, rgba(255,255,255,0) 6.5%)'
                            }}
                        />
                        <div className='absolute inset-0' style={{ background: edge }} />
                    </div>

                    {/* 4 — THE RIM, painted last. It has to be its own element
                        and it has to come after everything: an inset box-shadow
                        paints below its element's children, and the disc's first
                        child is an opaque copy of the page. `borderRadius:
                        inherit`, NOT a fixed circle: the disc's radius swings
                        every frame, and a fixed 50% here would trace a shape the
                        silhouette no longer has. */}
                    <div
                        ref={rimRef}
                        className='pointer-events-none absolute inset-0'
                        style={{ borderRadius: 'inherit', boxShadow: RIM_SHADOW }}
                    />
                    {/* …and the aimed band over it: bright toward the lamp, dark away */}
                    <div aria-hidden style={rimRing(2.2, rimLit, rimMid, rimFar)} />
                </div>
            </div>
        </section>
    )
}

export default Loupe
